import API from "../models/API";

export { LicenseService, LicenseDetails };

type LicenseDetails = {
  expiry: number | null;
  reportExpiry: number | null;
  tables: boolean;
  reporting: boolean;
  helios: boolean;
  transact: boolean;
  pageCount: number | null;
  usage: number | null;
};

class LicenseService {
  static _cachedDetails?: LicenseDetails;
  static _prom?: PromiseLike<LicenseDetails>;

  /**
   * Gets LicenseDetails from endpoint on first call to this method. Return cached value on subsequent calls.
   */
  static async getLicenseDetails(): Promise<LicenseDetails> {
    if (
      LicenseService._cachedDetails &&
      LicenseService._cachedDetails.pageCount != null
    )
      return LicenseService._cachedDetails;

    if (this._prom) {
      return await new Promise((res) =>
        this._prom?.then(() => res(LicenseService._cachedDetails!)),
      );
    }

    this._prom = new Promise(async (res, rej) => {
      const details: LicenseDetails = {
        expiry: null,
        reportExpiry: null,
        tables: false,
        reporting: false,
        helios: false,
        transact: true,
        pageCount: null,
        usage: null,
      };
      const licServ = new LicenseService();
      details.expiry = await licServ.getExpiry();
      details.tables = await licServ.isTablesLicensed();
      details.reporting = await licServ.isReportingLicensed();
      details.reportExpiry = await licServ.getReportingExpiry();
      details.helios = await licServ.isHelios();
      details.transact = await licServ.isTransact();
      details.pageCount = await licServ.getPageCount();
      details.usage = await LicenseService.getPageUsage();
      LicenseService._cachedDetails;
      res(details);
    });

    LicenseService._cachedDetails = await this._prom;

    return LicenseService._cachedDetails;
  }

  async isTablesLicensed(): Promise<boolean> {
    let licensed = false;
    try {
      const req = await API.get("/v0/license/tables");
      if (req.ok) {
        const json = await req.json();
        // evaluate returned JSON as a truthy value
        licensed = json ? true : false;
      }
    } catch (e) {
      console.error(e);
    }
    return licensed;
  }

  /**
   * @returns `null` if expiry date could not be returned.
   */
  async getExpiry(): Promise<number | null> {
    let expiry = null;
    try {
      const req = await API.get("/v0/license/expiry");
      const json = await req.json();
      if (Number.isInteger(json)) {
        expiry = json;
      }
    } catch (e) {
      console.error(e);
    }
    return expiry;
  }

  async isReportingLicensed(): Promise<boolean> {
    let reporting = false;
    try {
      const req = await API.get("/v0/license/reports");
      const json = await req.json();
      reporting = json ? true : false;
    } catch (e) {
      console.error(e);
    }
    return reporting;
  }

  async getReportingExpiry(): Promise<number | null> {
    let reportExpiry = null;
    try {
      const req = await API.get("/v0/license/reportexpiry");
      const json = await req.json();
      if (Number.isInteger(json)) {
        reportExpiry = json;
      }
    } catch (e) {
      console.error(e);
    }
    return reportExpiry;
  }

  async isHelios(): Promise<boolean> {
    let helios = false;
    try {
      const req = await API.get("/v0/license/helios");
      const json = await req.json();
      helios = json ? true : false;
    } catch (e) {
      console.error(e);
    }
    return helios;
  }

  async getPageCount(): Promise<number | null> {
    let pageCount = null;
    try {
      const req = await API.get("/v0/license/pageCount");
      const txt = await req.text();
      if (Number.isInteger(Number.parseInt(txt))) {
        pageCount = Number.parseInt(txt);
      } else if (txt === "UNLIMITED") {
        pageCount = -1;
      }
    } catch (e) {
      console.error(e);
    }
    return pageCount;
  }

  static async getPageUsage(): Promise<number | null> {
    let pageCount = null;
    try {
      const req = await API.get("/v0/license/usage");
      const json = await req.json();
      if (Number.isInteger(json)) {
        pageCount = json;
      }
    } catch (e) {
      console.error(e);
    }
    return pageCount;
  }

  async isTransact(): Promise<boolean> {
    return !(await this.isHelios());
  }
}
